import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Divider,
  Grid,
  Card,
  CardContent,
  TablePagination,
  Skeleton,
} from "@mui/material";
import { X, Package, Calendar } from "lucide-react";
import { alpha } from "@mui/material";
import formatDate from "../../../../Utils/formatDate";
import { useGetList } from "../../../../hooks/useGetList";
import { useDispatch, useSelector } from "react-redux";
import buildParams from "../../../../Utils/buildParams";
import {
  setLimit,
  setPage,
} from "../../../../redux/stockBatch/fitlerStockBatchSlice";
import StockBatchFilter from "../ProductItemUnit/StockBatchFilter";

const BatchLoadingSkeleton = () => (
  <>
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {[0, 1].map((i) => (
        <Grid item xs={12} md={3} key={i}>
          <Card
            sx={{
              backgroundColor: i === 0 ? "#f0fdf4" : "#fefce8",
              border: i === 0 ? "1px solid #bbf7d0" : "1px solid #fef3c7",
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Skeleton variant="circular" width={24} height={24} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="100%" height={16} />
                  <Skeleton variant="text" width="60%" height={24} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
    <Divider sx={{ mb: 3 }} />
    <Box sx={{ mb: 3 }}>
      <Skeleton
        variant="rectangular"
        width="100%"
        height={60}
        sx={{ borderRadius: 2 }}
      />
    </Box>
    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f8fafc" }}>
            {[
              "Code",
              "Initial Qty",
              "Out Qty",
              "Current Qty",
              "Type",
              "Purchased Date",
              "Expired Date",
              "Status",
            ].map((col) => (
              <TableCell key={col} sx={{ fontWeight: 600 }}>
                <Skeleton
                  variant="text"
                  width={col === "Purchased Date" ? 100 : 70}
                  height={20}
                />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(5)].map((_, rowIdx) => (
            <TableRow key={rowIdx}>
              {[...Array(8)].map((__, cellIdx) => (
                <TableCell key={cellIdx}>
                  <Skeleton
                    variant={cellIdx === 4 ? "rectangular" : "text"}
                    width={cellIdx === 7 ? 80 : 70}
                    height={cellIdx === 4 ? 24 : 20}
                    sx={cellIdx === 4 ? { borderRadius: 1 } : undefined}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </>
);

const StockCountAndPriceBatchDialog = ({
  open,
  onClose,
  selectedProductItemUnit,
  id,
  selectedBranch,
}) => {
  const dispatch = useDispatch();
  const filter = useSelector((state) => state.filterStockBatch);

  const param = buildParams(filter);
  const hasBranch = !!selectedBranch;
  const endpoint = hasBranch
    ? `stock-batch?relatedItem=${id}&relatedUnitInfo=${selectedProductItemUnit?._id}&relatedBranch=${selectedBranch}`
    : `stock-batch?relatedItem=${id}&relatedUnitInfo=${selectedProductItemUnit?._id}`;
  const { refetch, fetchData, loading, data } = useGetList(
    endpoint,
    id && selectedProductItemUnit?._id ? true : false,
    hasBranch && Object.keys(param).length > 0 ? param : undefined,
  );

  useEffect(() => {
    if (open && id && selectedProductItemUnit?._id) {
      refetch();
    }
  }, [open, refetch]);

  useEffect(() => {
    if (hasBranch) {
      fetchData(Object.keys(param).length > 0 ? param : undefined);
    }
  }, [filter]);

  const getTotalQuantity = () => {
    if (!data || !data.data) return 0;
    return data.data.reduce((total, batch) => total + (batch.qty || 0), 0);
  };

  const getBatchStatus = (expiredDate) => {
    if (!expiredDate) return { label: "No Expiry", color: "default" };
    const today = new Date();
    const expiry = new Date(expiredDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    if (daysUntilExpiry < 0) return { label: "Expired", color: "error" };
    if (daysUntilExpiry <= 30)
      return { label: "Expiring Soon", color: "warning" };
    return { label: "Good", color: "success" };
  };

  const handleChangePage = (e, page) => {
    dispatch(setPage(page + 1));
  };

  const handleChangeRowsPerPage = (e) => {
    dispatch(setLimit(e.target.value));
    dispatch(setPage(1));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      <Box
        sx={{
          background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
          px: 3,
          py: 2.5,
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <X size={20} />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, pr: 4 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Package size={24} color="white" />
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{ color: "white", fontWeight: 600, mb: 0.5 }}
            >
              Stock Batch Details
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.9)" }}
            >
              {selectedProductItemUnit?.relatedItem?.medicineItemName ||
                "Unknown Unit"}{" "}
              - {selectedProductItemUnit?.relatedUnit?.name || ""}
            </Typography>
          </Box>
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {loading ? (
          <BatchLoadingSkeleton />
        ) : !data || !data.data || data.data.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Package
              size={48}
              color="#94a3b8"
              style={{ margin: "0 auto 1rem" }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Stock Batches Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              There are no stock batches available for this unit at the selected
              branch.
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card
                  sx={{
                    backgroundColor: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{ color: "#16a34a" }}>
                        <Package size={24} />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="#64748b">
                          Current Quantity
                        </Typography>
                        <Typography
                          variant="h6"
                          color="#14532d"
                          fontWeight={600}
                        >
                          {getTotalQuantity().toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card
                  sx={{
                    backgroundColor: "#fefce8",
                    border: "1px solid #fef3c7",
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box sx={{ color: "#ca8a04" }}>
                        <Calendar size={24} />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="#64748b">
                          Total Batches
                        </Typography>
                        <Typography
                          variant="h6"
                          color="#713f12"
                          fontWeight={600}
                        >
                          {data.data.length}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            <StockBatchFilter />

            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Initial Qty</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Out Qty</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Current Qty</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Purchased Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Expired Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.data.map((batch, index) => {
                    const batchStatus = getBatchStatus(batch.expiredDate);
                    return (
                      <TableRow
                        key={batch._id || index}
                        sx={{
                          "&:hover": {
                            backgroundColor: (theme) =>
                              alpha(theme.palette.primary.main, 0.04),
                          },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {batch.code || `SB-${String(index + 1)}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {batch.initialQty?.toLocaleString() || 0}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            color="error.main"
                          >
                            {batch.outQty?.toLocaleString() || 0}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="success.main"
                          >
                            {batch.qty?.toLocaleString() || 0}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={batch.type || "N/A"}
                            color={
                              batch.type === "initial" ? "primary" : "secondary"
                            }
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {batch.purchasedDate
                              ? formatDate(batch.purchasedDate)
                              : "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {batch.expiredDate
                              ? formatDate(batch.expiredDate)
                              : "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={batchStatus.label}
                            color={batchStatus.color}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={data?.pagination?.total || 0}
              rowsPerPage={filter.limit}
              page={filter.page - 1}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                "& .MuiTablePagination-toolbar": {
                  display: "flex",
                  alignItems: "center",
                  minHeight: "52px",
                },
                "& .MuiTablePagination-selectLabel": {
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiTablePagination-displayedRows": {
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiTablePagination-actions": {
                  display: "flex",
                  alignItems: "center",
                },
              }}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockCountAndPriceBatchDialog;
